import { useState, useEffect } from "react";

// Bump when the optimized-image pipeline changes (e.g. EXIF rotation) so
// browsers that cached an old webp request a fresh one.
const IMAGE_CACHE_VERSION = "v2";

const optimizedImageUrl = (size, imageName) =>
  `${process.env.REACT_APP_API_URL}/api/optimized-images/${size}/${imageName}?v=${IMAGE_CACHE_VERSION}`;

const loadImage = (imageName, API_URL) => {
  const imagePath = `${API_URL}/images/${imageName}`;
  return new Promise((resolve, reject) => {
    const imageElement = new Image();
    imageElement.onload = () => resolve(imagePath);
    imageElement.onerror = (error) => reject(error);
    imageElement.src = imagePath;
  });
};

const useIsMobile = (breakpoint = 700) => {
  // Если "window" недоступен (SSR), считается, что ширина неизвестна. Начальное значение false.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= breakpoint;
    }
    return false;
  });

  useEffect(() => {
    // Для серверных условий `window` недоступен: пропускаем хук.
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const isCurrentlyMobile = window.innerWidth <= breakpoint;
      setIsMobile((prevIsMobile) =>
        prevIsMobile !== isCurrentlyMobile ? isCurrentlyMobile : prevIsMobile,
      );
    };

    // Вызов обработчика при монтировании компонента (для синхронизации)
    handleResize();

    // Добавление слушателя события
    window.addEventListener("resize", handleResize);

    // Удаление слушателя при размонтировании компонента
    return () => window.removeEventListener("resize", handleResize);
  });

  return isMobile;
};

const useElementVisibility = (ref, refName, callback, offset = 0) => {
  useEffect(() => {
    if (typeof window === "undefined" && !ref.current[refName]) return;

    const onWindowScroll = () => {
      const element = ref.current?.[refName];
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const isVisible = rect.top - offset <= 0;

      callback(isVisible);
    };

    // Слушаем событие прокрутки
    window.addEventListener("scroll", onWindowScroll);

    // Убираем слушатель при размонтировании
    return () => {
      window.removeEventListener("scroll", onWindowScroll);
    };
  }, [ref, callback, offset, refName]); // Зависимости
};

export {
  useIsMobile,
  loadImage,
  useElementVisibility,
  optimizedImageUrl,
};
